/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'throttle-debounce';
import Button from '@welldev-group/rcl-button';
import { CheckBoxFilter } from '@welldev-group/rcl-filter';
import httpClient from '../../libraries/httpClient';
import routes from '../../routes';

const searchOptions = async (type, filterParams, query, perPage) => {
  const { data } = await httpClient.get(
    routes.dropdownOptions.list({
      type,
      filterParams,
      q: query,
      per_page: perPage,
    }),
  );
  return data.options;
};

const onSearchOptionsFactory = ({ type, filterParams, callback }) =>
  debounce(500, async query => {
    const options = await searchOptions(type, filterParams, query);
    callback(options);
  });

const DropDownFilter = ({
  label,
  preIcon,
  type,
  onInitialFetch,
  selectedOptions,
  onChange,
  filterParams,
  onRestructuringDropDownOptions,
  ...otherProps
}) => {
  const [options, setOptions] = useState([]);
  const [searchParam, setSearchParam] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  const onOutsideClick = () => setIsVisible(false);

  const labelText = selectedOptions?.length
    ? selectedOptions.map((option, index) => (
        <>
          {index > 0 ? ', ' : null}
          {option.label}
        </>
      ))
    : label;

  const restructureDropDownOptions = fetchedOptions => {
    if (onRestructuringDropDownOptions) {
      onRestructuringDropDownOptions(fetchedOptions, setOptions);
    } else {
      setOptions(fetchedOptions);
    }
  };

  const onSearchOptions = React.useRef(
    onSearchOptionsFactory({
      type,
      filterParams,
      callback: restructureDropDownOptions,
    }),
  ).current;

  const onSearch = query => {
    setSearchParam(query);
    onSearchOptions(query);
  };

  const fetchInitialOptions = async () => {
    const fetchedOptions = await searchOptions(type, filterParams, '', 5);
    restructureDropDownOptions(fetchedOptions);
    return fetchedOptions;
  };

  useEffect(() => {
    fetchInitialOptions().then(onInitialFetch);
  }, [JSON.stringify(filterParams)]);

  const onSelectOption = selectedOption => {
    const newSelectedOptions = [...selectedOptions];

    const indexOfSelected = newSelectedOptions.findIndex(
      option => option.value === selectedOption.value,
    );
    if (indexOfSelected >= 0) {
      newSelectedOptions.splice(indexOfSelected, 1);
    } else {
      newSelectedOptions.push(selectedOption);
    }

    onChange(newSelectedOptions);
  };

  return (
    <div className="rcl-checkbox">
      <CheckBoxFilter
        isVisible={isVisible}
        onOutsideClick={onOutsideClick}
        onChange={onSelectOption}
        popoverMenuPosition="bottom-right"
        options={options}
        isSearchable={true}
        searchParam={searchParam}
        onSearch={onSearch}
        selectedOptions={selectedOptions}
        {...otherProps}
      >
        <Button
          label={
            <div className="rcl-filter__label">
              {preIcon}
              <span className="rcl-filter__label-text">{labelText}</span>
            </div>
          }
          size="small"
          onClick={() => {
            setIsVisible(previousIsVisibleState => !previousIsVisibleState);
          }}
        />
      </CheckBoxFilter>
    </div>
  );
};

DropDownFilter.defaultProps = {
  onInitialFetch: () => {},
  filterParams: {},
  onRestructuringDropDownOptions: null,
};

DropDownFilter.propTypes = {
  label: PropTypes.string.isRequired,
  preIcon: PropTypes.node.isRequired,
  type: PropTypes.string.isRequired,
  onInitialFetch: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  filterParams: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
    }),
  ).isRequired,
  onRestructuringDropDownOptions: PropTypes.func,
};

export default DropDownFilter;
